import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  useDisclosure,
  VStack
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getCorporateBeneficiaries,
  createCorporateBeneficiary
} from '@store/actions/libraDashboardActions';
import useAuthRole from '@hooks/useAuthRole';
import { ROLES } from '@constants';
import { updateLibraCorporateBeneficiariesReqData } from '@store/actions/reqDataActions';
import Pagination from '@components/Pagination';
import ModalLayout from '@components/Modal/ModalLayout';
import DynamicInputModal from '@components/DynamicInputModal';
import ConfirmModal from '@components/Modal/ConfirmModal';
import DataTable from '@components/DataTable';
import { MdAdd } from 'react-icons/md';
import { getCorporateBeneficiaryFields } from './formConfigs';
import { getCorporateBeneficiariesColumns } from './tableColumns';
import { buildCorporateBeneficiaryRequestData } from './utils';

export const CorporateBeneficiariesTab = ({ selectedItem, onItemClick }) => {
  const dispatch = useDispatch();
  const { hasAuthority } = useAuthRole();
  const isBackofficeManager = hasAuthority(ROLES.BACKOFFICE_MANAGER_ROLE);

  // Redux state
  const corporateBeneficiaries = useSelector(
    (state) => state.libraDashboard.corporateBeneficiaries
  );
  const createCorporateBeneficiaryState = useSelector(
    (state) => state.libraDashboard.createCorporateBeneficiary
  );
  const reqData = useSelector((state) => state.reqData.libraDashboard.corporateBeneficiaries);

  // Local state
  const [selectedTransferType, setSelectedTransferType] = useState(null);
  const [pendingFormData, setPendingFormData] = useState(null);

  const {
    isOpen: isCreateModalOpen,
    onClose: closeCreateModal,
    onOpen: openCreateModal
  } = useDisclosure();

  const {
    isOpen: isConfirmModalOpen,
    onClose: closeConfirmModal,
    onOpen: openConfirmModal
  } = useDisclosure();

  useEffect(() => {
    fetchCorporateBeneficiaries();
  }, [reqData.pageNumber]);

  const fetchCorporateBeneficiaries = () => {
    const params = {
      page: reqData.pageNumber,
      page_size: reqData.pageSize
    };
    dispatch(getCorporateBeneficiaries(params));
  };

  const handlePageChange = (page) => {
    dispatch(updateLibraCorporateBeneficiariesReqData({
      ...reqData,
      pageNumber: page - 1
    }));
  };

  // Get dynamic fields based on selected transfer type
  const createCorporateBeneficiaryFields = getCorporateBeneficiaryFields(selectedTransferType);

  // Handle field changes
  const handleFieldChange = (fieldName, value) => {
    if (fieldName === 'transferType') {
      setSelectedTransferType(value);
    }
  };

  // Reset state when modal closes
  const handleCloseModal = () => {
    setSelectedTransferType(null);
    closeCreateModal();
  };

  // Called when form is submitted - shows confirmation modal
  const handleFormSubmit = (formData) => {
    setPendingFormData(formData);
    closeCreateModal();
    openConfirmModal();
  };

  // Called when confirmation is confirmed
  const handleConfirmCreate = () => {
    if (!pendingFormData) return;

    const reqData = buildCorporateBeneficiaryRequestData(pendingFormData);

    dispatch(
      createCorporateBeneficiary(reqData, () => {
        closeConfirmModal();
        setPendingFormData(null);
        setSelectedTransferType(null);
        fetchCorporateBeneficiaries();
      })
    );
  };

  // Called when confirmation is cancelled
  const handleCancelConfirm = () => {
    closeConfirmModal();
    setPendingFormData(null);
  };

  return (
    <Box position={'relative'} overflowX={'hidden'} flex={1}>
      {/* Create Corporate Beneficiary Modal */}
      <ModalLayout
        isOpen={isCreateModalOpen}
        onClose={handleCloseModal}
        size="xl"
        scrollBehavior="inside"
      >
        <DynamicInputModal
          title="Create Corporate Beneficiary"
          onClose={handleCloseModal}
          onSubmit={handleFormSubmit}
          fields={createCorporateBeneficiaryFields}
          isLoading={createCorporateBeneficiaryState.isLoading}
          onFieldChange={handleFieldChange}
        />
      </ModalLayout>

      {/* Confirmation Modal */}
      <ModalLayout
        isOpen={isConfirmModalOpen}
        onClose={handleCancelConfirm}
        size="md"
      >
        <ConfirmModal
          handleSubmit={handleConfirmCreate}
          isLoading={createCorporateBeneficiaryState.isLoading}
          prompt="Are you sure you want to create this corporate beneficiary?"
        />
      </ModalLayout>

      <VStack align="stretch" spacing={4}>
        {/* Header with heading and actions */}
        <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
          <Heading size="lg" color="colorPrimary">
            Corporate Beneficiaries
          </Heading>
          {isBackofficeManager && (
            <Button
              leftIcon={<MdAdd />}
              colorScheme="brand"
              size="sm"
              onClick={openCreateModal}
              border="2px solid"
              borderColor="whiteAlpha.500"
              borderRadius="lg"
              bg="brand.600"
              _hover={{ bg: 'brand.700' }}
            >
              Create Beneficiary
            </Button>
          )}
        </Flex>
        {/* Pagination */}
        {!corporateBeneficiaries.isLoading && corporateBeneficiaries?.data?.items?.length > 0 && (
          <Flex mt={4}>
            <Pagination
              totalPages={corporateBeneficiaries?.data?.maxPages || 1}
              currentPage={reqData.pageNumber + 1}
              onPageChange={handlePageChange}
            />
          </Flex>
        )}
        {/* Corporate Beneficiaries Table */}
        <DataTable
          data={corporateBeneficiaries?.data?.items || []}
          columns={getCorporateBeneficiariesColumns(onItemClick, selectedItem)}
          isLoading={corporateBeneficiaries.isLoading}
          noDataFoundMessage="No corporate beneficiaries found"
          onRowClick={(beneficiary) => onItemClick(beneficiary, [])}
          selectedRow={selectedItem}
          rowKeyField="corporateBeneficiaryId"
        />
      </VStack>
    </Box>
  );
};
